import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Phone, Mail, Users, FileText, Plus, Trash2, MessageSquare, ArrowRightLeft, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPE_CONFIG = {
  rozmowa: { icon: Phone, color: 'text-blue-400', bg: 'bg-blue-500/15', dot: 'bg-blue-400' },
  email: { icon: Mail, color: 'text-purple-400', bg: 'bg-purple-500/15', dot: 'bg-purple-400' },
  spotkanie: { icon: Users, color: 'text-amber-400', bg: 'bg-amber-500/15', dot: 'bg-amber-400' },
  notatka: { icon: FileText, color: 'text-[var(--crm-text-secondary)]', bg: 'bg-slate-500/15', dot: 'bg-slate-400' },
  status: { icon: ArrowRightLeft, color: 'text-[#FF6B35]', bg: 'bg-[#FF6B35]/10', dot: 'bg-[#FF6B35]' },
  komentarz: { icon: MessageSquare, color: 'text-emerald-400', bg: 'bg-emerald-500/15', dot: 'bg-emerald-400' },
  history: { icon: Activity, color: 'text-[var(--crm-text-muted)]', bg: 'bg-slate-500/10', dot: 'bg-slate-500' },
};

export const ContactHistory = ({ entityType, entityId }) => {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [contactType, setContactType] = useState('notatka');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/contacts/${entityType}/${entityId}`);
      setEntries(res.data);
    } catch { /* silently fail */ }
    finally { setLoading(false); }
  }, [entityType, entityId]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleAdd = async () => {
    if (!content.trim()) { toast.error('Treść jest wymagana'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/contacts`, {
        entity_type: entityType, entity_id: entityId,
        contact_type: contactType, content: content.trim()
      });
      toast.success('Wpis dodany');
      setContent('');
      setShowForm(false);
      fetchEntries();
    } catch { toast.error('Błąd dodawania wpisu'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Usunąć ten wpis?')) return;
    try {
      await axios.delete(`${API_URL}/contacts/${id}`);
      toast.success('Wpis usunięty');
      fetchEntries();
    } catch { toast.error('Błąd usuwania'); }
  };

  const formatDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleString('pl-PL', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch { return d; }
  };

  const isDemoMode = user?.role === 'duch';

  return (
    <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6" data-testid="contact-history-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Phone className="w-5 h-5 text-[#FF6B35]" />
          Historia kontaktów
        </h2>
        {!isDemoMode && user?.role !== 'guest' && (
          <Button size="sm" onClick={() => setShowForm(!showForm)} data-testid="add-contact-btn"
            className="bg-[#FF6B35] hover:bg-[#e55a2a] text-white text-xs">
            <Plus className="w-3.5 h-3.5 mr-1" />{showForm ? 'Anuluj' : 'Dodaj kontakt'}
          </Button>
        )}
      </div>

      {/* Add form */}
      {showForm && (
        <div className="mb-5 p-4 bg-[var(--crm-bg-panel)] rounded-lg border border-[var(--crm-border)] space-y-3" data-testid="contact-form">
          <div className="flex gap-3">
            <Select value={contactType} onValueChange={setContactType}>
              <SelectTrigger className="bg-[var(--crm-bg-main)] border-[var(--crm-border)] text-white w-44 h-9" data-testid="contact-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)]">
                <SelectItem value="rozmowa" className="text-white">Rozmowa tel.</SelectItem>
                <SelectItem value="email" className="text-white">Email</SelectItem>
                <SelectItem value="spotkanie" className="text-white">Spotkanie</SelectItem>
                <SelectItem value="notatka" className="text-white">Notatka</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Textarea value={content} onChange={(e) => setContent(e.target.value)}
            placeholder="Opisz kontakt z klientem..." rows={3} data-testid="contact-content-input"
            className="bg-[var(--crm-bg-main)] border-[var(--crm-border)] text-white" />
          <div className="flex justify-end">
            <Button size="sm" onClick={handleAdd} disabled={saving} data-testid="contact-save-btn"
              className="bg-[#FF6B35] hover:bg-[#e55a2a] text-white">
              {saving ? 'Zapisywanie...' : 'Zapisz'}
            </Button>
          </div>
        </div>
      )}

      {/* Timeline */}
      {loading ? (
        <p className="text-[var(--crm-text-secondary)] text-sm">Ładowanie...</p>
      ) : entries.length === 0 ? (
        <p className="text-[var(--crm-text-muted)] text-sm text-center py-6">Brak historii kontaktów</p>
      ) : (
        <div className="relative">
          <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-[var(--crm-bg-btn-secondary)]" />
          <div className="space-y-3">
            {entries.map((e) => {
              const cfg = TYPE_CONFIG[e.contact_type] || TYPE_CONFIG.history;
              const Icon = cfg.icon;
              const canDelete = e.type === 'contact' && (e.author_email === user?.email || user?.role === 'admin');
              return (
                <div key={e.id} className="relative pl-8" data-testid={`contact-entry-${e.id}`}>
                  <div className={`absolute left-[6px] top-2 w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
                  <div className={`rounded-lg p-3 ${cfg.bg} border border-transparent`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap text-xs">
                        <Icon className={`w-3.5 h-3.5 ${cfg.color} shrink-0`} />
                        <span className={`font-medium ${cfg.color}`}>{e.contact_type_label}</span>
                        <span className="text-[var(--crm-text-muted)]">{formatDate(e.created_at)}</span>
                        {e.author_initials && (
                          <Badge className="bg-[var(--crm-bg-btn-secondary)] text-white text-[10px] px-1.5 py-0">{e.author_initials}</Badge>
                        )}
                      </div>
                      {canDelete && (
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(e.id)}
                          className="h-6 w-6 p-0 text-[var(--crm-text-muted)] hover:text-red-400 shrink-0">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                    <p className="text-white text-sm mt-1 whitespace-pre-wrap">{e.content}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
};
